import React, { useState } from 'react';
import cx from 'classnames';
import { useIntl } from 'react-intl';
import { BlockDataForm } from '@plone/volto/components/manage/Form';
import { default as SidebarPortal } from '@plone/volto/components/manage/Sidebar/SidebarPortal';

import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import { isInternalURL } from '@plone/volto/helpers/Url/Url';

import StringToHTML from '../../StringToHTML/StringToHTML';
import ButtonView from '../Button/View';
import { buttonBlockSchema } from './featured_schema_enhancer';
import { emptyButtons, getPanels } from './utils';
import { isEmpty } from 'lodash';
import { useInView } from 'react-intersection-observer';
import { Helmet } from '@plone/volto/helpers/Helmet/Helmet';
import { sanitizedHTML } from '../../../helpers/sanitizedHTML';

export function getTeaserImageURL(href, image) {
  let image_url = '';
  if (image) {
    if (isInternalURL(image['@id'])) {
      image_url = image?.image_scales?.image?.[0]?.scales?.great?.download
        ? `${image['@id']}/${image?.image_scales?.image?.[0]?.scales?.great?.download}`
        : `${image['@id']}/@@images/image/great`;
    } else {
      image_url = image['@id'];
    }
  } else {
    image_url = `${href['@id']}/@@images/${href.image_field || 'preview_image'}`;
  }
  return flattenToAppURL(image_url);
}
const FeaturedBaseView = (props) => {
  const {
    data,
    isEditMode,
    selected,
    onChangeBlock,
    block,
    className,
    style,
    HeadingTag = 'h2',
    centeredText = false,
    animatedHeading = false,
    headerSize = '',
    buttons = false,
  } = props;
  const { ref, inView } = useInView({
    /* Optional options */
    threshold: 0.5,
    triggerOnce: true,
  });
  const video = data.video?.[0]?.['@id'];
  const href = data.href?.[0];
  const image = data.preview_image?.[0];
  const [selectedButtonBlock, setSelectedButtonBlock] = useState(-1);
  const intl = useIntl();
  const properties = isEmpty(data?.buttons?.blocks)
    ? emptyButtons(1)
    : data.buttons;
  const panelData = properties;
  const panels = getPanels(panelData);
  React.useEffect(() => {
    if (isEmpty(data?.buttons)) {
      onChangeBlock(block, {
        ...data,
        buttons: {
          ...properties,
        },
      });
    }
    /* eslint-disable-next-line */
  }, []);

  return (
    <div
      id={props.id}
      className={cx(
        'featured-wrapper',
        headerSize ? `vh-100-${headerSize}-header` : '',
        centeredText ? 'vh-center-text' : '',
        className,
        inView && animatedHeading ? 'border animated-border' : 'border',
        video ? 'video-contained' : '',
      )}
      ref={ref}
      style={
        image
          ? {
              ...style,
              backgroundImage: `linear-gradient(rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.75)), url(${getTeaserImageURL(href, image)})`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              alignItems: 'end',
            }
          : { ...style }
      }
    >
      {video && (
        <>
          <Helmet>
            <link
              rel="preload"
              href={
                video.includes('/@@download/file')
                  ? video
                  : `${video}/@@download/file`
              }
              as="media"
            ></link>
          </Helmet>
          <video
            autoPlay="autoplay"
            muted="muted"
            className="embed-responsive-item"
            id="sliderHomeVideo1"
            loop="loop"
            playsInline="playsinline"
          >
            <source
              type="video/mp4"
              src={flattenToAppURL(
                video.includes('/@@download/file')
                  ? video
                  : `${video}/@@download/file`,
              )}
            />
            <track kind="captions" />
          </video>
        </>
      )}
      {image && HeadingTag === 'h1' && (
        <Helmet>
          <link
            rel="preload"
            href={getTeaserImageURL(href, image)}
            as="image"
          ></link>
        </Helmet>
      )}
      {((data.richtitle && sanitizedHTML(data?.richtitle.data)) ||
        !data.hideButtons ||
        sanitizedHTML(data?.description2)) && (
        <div className="featured-title-description-wrapper">
          <div className="featured-title-description">
            {data.richtitle && sanitizedHTML(data?.richtitle.data) && (
              <HeadingTag>
                <StringToHTML string={data?.richtitle?.data} />
              </HeadingTag>
            )}
            {sanitizedHTML(data?.description2) && (
              <div className="featured-description">
                <p>{data?.description2}</p>
              </div>
            )}
          </div>
          {buttons && !data.hideButtons && (
            <>
              <div className="buttons-row">
                {panels.map(([uid, panel], index) =>
                  isEditMode ? (
                    <div
                      key={index}
                      tabIndex={0}
                      role="button"
                      onClick={() => isEditMode && setSelectedButtonBlock(uid)}
                      onKeyDown={() =>
                        isEditMode && setSelectedButtonBlock(uid)
                      }
                    >
                      <ButtonView
                        className="transparent"
                        data={panel}
                        isEditMode={isEditMode}
                      />
                    </div>
                  ) : panel.title ? (
                    <ButtonView
                      key={index}
                      className="transparent"
                      data={panel}
                      isEditMode={isEditMode}
                    />
                  ) : (
                    <React.Fragment key={index} />
                  ),
                )}
              </div>
              {isEditMode && panels && selectedButtonBlock !== -1 && (
                <div>
                  <button onClick={() => setSelectedButtonBlock(-1)}>
                    Back to edit the featured item
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
      <SidebarPortal
        selected={
          selected && selectedButtonBlock !== -1 && data.buttons?.blocks
        }
      >
        <BlockDataForm
          schema={buttonBlockSchema(intl)}
          title="Button block"
          onChangeField={(id, value) => {
            onChangeBlock(block, {
              ...data,
              buttons: {
                ...data.buttons,
                blocks: {
                  ...data.buttons.blocks,
                  [selectedButtonBlock]: {
                    ...data.buttons.blocks[selectedButtonBlock],
                    [id]: value,
                  },
                },
              },
            });
          }}
          formData={data.buttons?.blocks?.[selectedButtonBlock]}
        />
      </SidebarPortal>
    </div>
  );
};

export default FeaturedBaseView;
